import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { sendFbConversionEvent } from 'src/features/analytics/utils/sendFbConversionEvent';
import { v4 as uuidv4 } from 'uuid';
import Bugsnag from '@bugsnag/js';
import { KEYWORDS } from 'src/constants/meta';
import { useHeadTags, useHead, escapeAttr } from 'src/core/engine/head';
import { scheduleIdle } from 'src/components/Scripts/scheduleIdle';

import { StoreState } from '../../types/global-types';
import { currentUrl } from '../../util/currentUrl';
import { useDeps } from '../../hooks/useDeps';

type HeadProps = Record<string, unknown>;

const TRIPLE_WHALE_PIXEL_SNIPPET =
    '/* >> TriplePixel :: start*/\n' +
    'window.TriplePixelData={TripleName:"www.workwearexpress.com",ver:"2.17",plat:"custom-msp",isHeadless:true},function(W,H,A,L,E,_,B,N){function O(U,T,P,H,R){void 0===R&&(R=!1),H=new XMLHttpRequest,P?(H.open("POST",U,!0),H.setRequestHeader("Content-Type","text/plain")):H.open("GET",U,!0),H.send(JSON.stringify(P||{})),H.onreadystatechange=function(){4===H.readyState&&200===H.status?(R=H.responseText,U.includes("/first")?eval(R):P||(N[B]=R)):(299<H.status||H.status<200)&&T&&!R&&(R=!0,O(U,T-1,P))}}if(N=window,!N[H+"sn"]){N[H+"sn"]=1,L=function(){return Date.now().toString(36)+"_"+Math.random().toString(36)};try{A.setItem(H,1+(0|A.getItem(H)||0)),(E=JSON.parse(A.getItem(H+"U")||"[]")).push({u:location.href,r:document.referrer,t:Date.now(),id:L()}),A.setItem(H+"U",JSON.stringify(E))}catch(e){}var i,m,p;A.getItem(\'"!nC`\')||(_=A,A=N,A[H]||(E=A[H]=function(t,e,a){return void 0===a&&(a=[]),"State"==t?E.s:(W=L(),(E._q=E._q||[]).push([W,t,e].concat(a)),W)},E.s="Installed",E._q=[],E.ch=W,B="configSecurityConfModel",N[B]=1,O("https://conf.config-security.com/model",5),i=L(),m=A[atob("c2NyZWVu")],_.setItem("di_pmt_wt",i),p={id:i,action:"profile",avatar:_.getItem("auth-security_rand_salt_"),time:m[atob("d2lkdGg=")]+":"+m[atob("aGVpZ2h0")],host:A.TriplePixelData.TripleName,plat:A.TriplePixelData.plat,url:window.location.href.slice(0,500),ref:document.referrer,ver:A.TriplePixelData.ver},O("https://api.config-security.com/event",5,p),O("https://api.config-security.com/first?host=www.workwearexpress.com&plat=custom-msp",5)))}}("","TriplePixel",localStorage);\n' +
    '/* << TriplePixel :: end*/';

const selector = (s: StoreState) => s.router.location.pathname;

export const Head: React.FC<HeadProps> = () => {
    const pathname = useSelector(selector, shallowEqual);
    const { DOMAIN } = useDeps().env;
    const url = currentUrl(DOMAIN!, pathname);
    const { resetHead } = useHeadTags();
    const prevPathname = useRef(pathname);

    // Clear accumulated meta/link on SPA navigations during render so child
    // page effects can re-append tags for the new route without duplicates.
    if (prevPathname.current !== pathname) {
        prevPathname.current = pathname;
        resetHead();
    }

    // FB CAPI off the render path — once per pathname, avoids SSR/render storms.
    useEffect(() => {
        const eventId = uuidv4();
        sendFbConversionEvent({
            data: [
                {
                    event_name: 'PageView',
                    event_time: Math.floor(Date.now() / 1000),
                    action_source: 'website',
                    event_source_url: url,
                    event_id: eventId,
                    user_data: {
                        external_id: [eventId],
                    },
                },
            ],
        }).catch((error) => {
            Bugsnag.notify(error);
        });
    }, [pathname, url]);

    // Client-only: inject third-party marketing scripts after idle so they
    // don't compete with first paint. Consent gating (OneTrust / optanon) kept.
    useEffect(() => {
        let tripleWhale: HTMLScriptElement | null = null;
        let fbScript: HTMLScriptElement | null = null;
        let oneTrust: HTMLScriptElement | null = null;
        let optanonWrapper: HTMLScriptElement | null = null;
        let trustpilot: HTMLScriptElement | null = null;

        const cancelIdle = scheduleIdle(() => {
            // OneTrust hard-codes its consent cookie domain to the production
            // hostname (`.workwearexpress.com`). On `localhost` the browser
            // rejects that cookie, so the consent banner re-shows on every page
            // load. Skip OneTrust + OptanonWrapper in dev. FB Pixel and Triple
            // Whale stay dormant until OneTrust converts their `text/plain` tags.
            const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

            const eventId = uuidv4();
            tripleWhale = document.createElement('script');
            tripleWhale.type = 'text/plain';
            tripleWhale.className = 'optanon-category-C0004';
            tripleWhale.textContent = TRIPLE_WHALE_PIXEL_SNIPPET;
            document.head.appendChild(tripleWhale);

            fbScript = document.createElement('script');
            fbScript.type = 'text/plain';
            fbScript.className = 'optanon-category-C0004';
            fbScript.textContent = `
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '533751407052024');
                fbq('track', 'PageView', null ,{eventID:'${eventId}'});
                fbq.disablePushState = true
                fbq.allowDuplicatePageViews = true
        `;
            document.head.appendChild(fbScript);

            if (!isLocalDev) {
                oneTrust = document.createElement('script');
                oneTrust.src = 'https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js';
                oneTrust.type = 'text/javascript';
                oneTrust.charset = 'UTF-8';
                (oneTrust as any).setAttribute('data-domain-script', '4aebcfe1-bb2f-4083-a8ba-6352c58b73e1');
                document.head.appendChild(oneTrust);
                optanonWrapper = document.createElement('script');
                optanonWrapper.type = 'text/javascript';
                optanonWrapper.textContent = 'function OptanonWrapper() {}';
                document.head.appendChild(optanonWrapper);
            }

            trustpilot = document.createElement('script');
            trustpilot.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
            trustpilot.async = true;
            trustpilot.type = 'text/javascript';
            document.head.appendChild(trustpilot);
        }, 3000);

        return () => {
            cancelIdle();
            tripleWhale?.remove();
            fbScript?.remove();
            oneTrust?.remove();
            optanonWrapper?.remove();
            trustpilot?.remove();
        };
    }, []);

    // Path-specific head only — static icons/meta/fonts live in SSR renderShell.
    useHead({
        link: [
            `<link rel="canonical" href="${escapeAttr(url)}" />`,
            '<link rel="preconnect dns-prefetch" href="https://api.config-security.com/" crossorigin="" />',
            '<link rel="preconnect dns-prefetch" href="https://conf.config-security.com/" crossorigin="" />',
        ].join(''),
        meta: [
            `<meta name="keywords" content="${escapeAttr(KEYWORDS)}" />`,
            `<meta property="og:url" content="${escapeAttr(url)}" />`,
        ].join(''),
    });

    return null;
};

interface Meta {
    data: {
        title: string;
        description: string;
    };
    withoutPrefix?: boolean;
}

export const DefaultMeta: React.FC<PropsWithChildren<Meta>> = ({ data, withoutPrefix }) => {
    const titleText = data.title ? `${data.title}${withoutPrefix ? '' : ' | Workwear Express'}` : '';
    const meta = [
        titleText ? `<meta name="title" content="${escapeAttr(data.title)}" />` : '',
        `<meta name="description" content="${escapeAttr(data.description || '')}" />`,
        `<meta property="og:title" content="${escapeAttr(data.title || '')}" />`,
        `<meta property="og:description" content="${escapeAttr(data.description || '')}" />`,
    ]
        .filter(Boolean)
        .join('');
    useHead({
        title: titleText ? `<title>${escapeAttr(titleText)}</title>` : '',
        meta: meta || undefined,
        link: undefined,
    });
    return null;
};
